import { ButtonVariants } from '@anm/components/buttons/Button';
import ButtonLoader from '@anm/components/buttons/ButtonLoader';
import FormHelperText from '@anm/components/form/FormHelperText';
import { DisplayNameField, EmailField } from '@anm/components/form/fields';
import compactProps from '@anm/helpers/compactProps';
import isDisplayName from '@anm/helpers/is/isDisplayName';
import isEmail from '@anm/helpers/is/isEmail';
import isUserExist from '@anm/helpers/is/isUserExist';
import { Config as FinalFormConfig } from 'final-form';
import memoize from 'lodash/fp/memoize';
import React, { FC } from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import { compose } from 'recompose';

import withUpdateUserHandlers from './HOCs/withUpdateUserHandlers';
import withUpdateUserInitialData, {
  UpdateUserInitialData
} from './HOCs/withUpdateUserInitialData';

type HOCs = FinalFormConfig & UpdateUserInitialData;
export type UpdateUserFormProps = {
  isPending: boolean;
  buttonVariant?: ButtonVariants;
  validateOnBlur?: boolean;
};

export type UpdateUserFormFields = {
  email: string;
  displayName: string;
};

const { Provider, Consumer } = React.createContext<
  Partial<UpdateUserFormProps>
>({});
export const EmailAuthFormProvider = Provider;

const memoizedIsUserExist = memoize(isUserExist);

const renderForm = (
  isPending: boolean,
  buttonText = 'Submit',
  buttonVariant: ButtonVariants = 'light-blue-lowercase'
) => ({ submitError, handleSubmit }: FormRenderProps) => (
  <form onSubmit={handleSubmit}>
    <EmailField />
    <DisplayNameField />

    {submitError && (
      <FormHelperText variant="belowField">{submitError}</FormHelperText>
    )}

    <ButtonLoader
      size="large"
      type="submit"
      variant={buttonVariant}
      isPending={isPending}
    >
      {buttonText || 'Submit'}
    </ButtonLoader>
  </form>
);

const validateForm = async ({ email, displayName }: UpdateUserFormFields) =>
  compactProps({
    email: !email
      ? 'This field is required'
      : !isEmail(email)
      ? 'Incorrect email'
      : (await memoizedIsUserExist(email))
      ? 'This email is already registered'
      : null,
    displayName: !displayName
      ? 'This field is required'
      : !isDisplayName(displayName)
      ? 'Incorrect name'
      : null
  });

const UpdateUserForm: FC<UpdateUserFormProps & HOCs> = ({
  isPending,
  initialData,
  children: buttonText,
  validateOnBlur = true,
  ...otherProps
}) => (
  <Consumer>
    {({ buttonVariant }) => (
      <Form
        render={renderForm(isPending, buttonText as string, buttonVariant)}
        validate={validateForm}
        initialValues={initialData}
        {...{ ...otherProps, validateOnBlur }}
      />
    )}
  </Consumer>
);

export default compose<HOCs, UpdateUserFormProps>(
  withUpdateUserHandlers,
  withUpdateUserInitialData
)(UpdateUserForm);
